import {
  findInWindowAncestors,
  isSameOriginWindow
} from 'common/js/window-ancestors';

export type ContextParameterName =
  | 'adUnitType'
  | 'inArticleType'
  | 'adsSetId'
  | 'executionEnvironment'
  | 'experienceId'
  | 'macros'
  | 'clickUrl'
  | 'click_url'
  | 'DSP_token'
  | 'impressionId'
  | 'creativeId'
  | 'campaignId'
  | 'creative_ratio'
  | 'click_strategy'
  | 'deviceOs'
  | 'is_programmatic'
  | 'apikey'
  | 'adUnitId'
  | 'isVpaidCreative';

export type ContextVariableName = 'context' | 'parameters';

export type HostSlotParameterName = 'adUnitType' | 'inArticleType';

/**
 * Window key on which vpaid.js publishes the rich-media context + parameters
 * when a creative-tag bundle is routed through a VPAID host (e.g. the video
 * format). The bundle shares the host's `window` (the VPAID container is a
 * `<div>`, not an iframe), so `window.context`/`window.parameters` already
 * belong to the host format and must NOT be reused. This dedicated channel is
 * collision-free and takes priority in {@link ParametersExtractor.getAdContext}.
 * It MUST stay byte-identical to the literal written by
 * Motionly-templates `ExternalSprites/VPAID/vpaid.js` (ADR 0001).
 */
export const VPAID_RICH_CONTEXT_WINDOW_KEY = '__OGURY_VPAID_RICH_CONTEXT__';

type VpaidRichContextChannel = { [key in ContextVariableName]?: Context };
type VpaidRichContextWindow = Window & {
  __OGURY_VPAID_RICH_CONTEXT__?: VpaidRichContextChannel;
};

type ContextMap = { [key: string]: ContextValue };
type ContextValue = string | number | ContextMap | boolean | undefined;
export type Context = {
  [key in ContextParameterName]?: ContextValue;
};
export type ContextAugmentedWindow = Window & {
  context: Context;
  parameters: ContextMap;
};

export class ParametersExtractor {
  public getWindowVariable<T>(varName: string, aWindow: Window): T {
    return findInWindowAncestors<T>(aWindow, currentWindow => {
      const value = (currentWindow as any)[varName];
      return value ? (value as T) : undefined;
    }) as T;
  }

  public getContextParameters<T extends ContextValue>(
    parameter: ContextParameterName,
    aWindow: Window,
    contextVariableName?: ContextVariableName,
    maxHops?: number
  ): T {
    return findInWindowAncestors<T>(
      aWindow,
      currentWindow => {
        const context = this.getAdContext(currentWindow, contextVariableName);
        return context && context[parameter] !== undefined
          ? (context[parameter] as T)
          : undefined;
      },
      maxHops
    ) as T;
  }

  // Reads `parameter` from the first ancestor context that holds `anchor`, so
  // values the ad server writes together come from one window.
  public getContextParameterBeside<T extends ContextValue>(
    parameter: ContextParameterName,
    anchor: ContextParameterName,
    aWindow: Window
  ): T | undefined {
    const context = findInWindowAncestors<Context>(aWindow, currentWindow => {
      const candidate = this.getAdContext(currentWindow);
      return candidate?.[anchor] !== undefined ? candidate : undefined;
    });
    return context?.[parameter] as T | undefined;
  }

  // For /experience rule matching only: rendering must keep treating a VPAID-hosted bundle as third party.
  public getHostSlotParameter<T extends string | boolean>(
    parameter: HostSlotParameterName,
    aWindow: Window
  ): T | undefined {
    const value = this.getContextParameters<T>(parameter, aWindow);
    if (value !== undefined) {
      return value;
    }
    // getAdContext returns the VPAID channel instead of the host `window.context` next to it, and the channel has no slot info.
    const hostContext = this.getWindowVariable<Context | undefined>(
      'context',
      aWindow
    );
    return hostContext?.[parameter] as T | undefined;
  }

  public getAdContext(
    aWindow: Window | undefined,
    contextVariableName: ContextVariableName = 'context'
  ): Context | undefined {
    if (!aWindow) {
      return undefined;
    }

    // A creative-tag bundle routed through a VPAID host shares the host's
    // `window`, whose `context`/`parameters` belong to the host format. When
    // present, vpaid.js's dedicated channel takes priority (ADR 0001).
    const vpaidChannel = (aWindow as VpaidRichContextWindow)[
      VPAID_RICH_CONTEXT_WINDOW_KEY
    ];
    if (vpaidChannel && vpaidChannel[contextVariableName] !== undefined) {
      return vpaidChannel[contextVariableName];
    }

    if (
      (aWindow as ContextAugmentedWindow)[contextVariableName] === undefined
    ) {
      return undefined;
    }

    return (aWindow as ContextAugmentedWindow)[contextVariableName];
  }

  public canAccessGrandParentWindow(aWindow: Window): boolean {
    // Contract: "parent.parent.document reachable" (see EnvironmentReads in
    // environment-detection.ts). ONLY the grandparent must be same-origin; the
    // intermediate parent does not (it may legitimately be the top window, where
    // parent.parent === parent — a one-level iframe inside a demo page). That is why
    // we reuse the shared same-origin primitive on the grandparent directly rather
    // than canAccessParentWindow(aWindow.parent), which would add a wrong
    // `parent.parent !== parent` self-check and break isRunningInDemoPage's
    // `parent.parent.isOgyDemoPage` read.
    try {
      const parent = (aWindow.parent as unknown) as { parent?: unknown };
      return isSameOriginWindow(parent.parent);
    } catch (_) {
      return false;
    }
  }
}
